import React from 'react';
import { TemplateType } from '../../types/inAppMessageTypes';
import { Wifi, Battery, Signal } from 'lucide-react';
import { usePreferences } from '../../contexts/PreferencesContext';
import { getThemeColors } from '../../config/theme';

export const PhonePreview = ({ config }) => {
  const { isDarkMode } = usePreferences();
  const colors = getThemeColors(isDarkMode);
  const { type, content, styles } = config;

  const renderContent = () => {
    const isFull = type === TemplateType.FULL_SCREEN;
    const isCenter = type === TemplateType.CENTER_MODAL;
    const showSecondary = (isFull || isCenter) && content.showSecondaryButton;
    const showImage = content.showImage && content.imageUrl;

    return (
      <div className={`flex flex-col ${isFull ? 'h-full' : 'h-auto'}`}>
        {showImage && (
          <img 
            src={content.imageUrl} 
            alt="Hero" 
            className={`w-full object-cover flex-shrink-0 ${
              isFull ? 'h-64' : 'h-48'
            }`}
            style={{ 
              borderTopLeftRadius: !isFull ? styles.borderRadius : 0,
              borderTopRightRadius: !isFull ? styles.borderRadius : 0
            }}
          />
        )}
        
        <div className={`flex flex-col ${isFull ? 'flex-1 justify-center p-8' : 'p-6'}`}>
          <h3 
            className="font-bold mb-3 leading-tight"
            style={{ 
              color: styles.textColor,
              fontSize: `${styles.titleFontSize}px`
            }}
          >
            {content.title || 'Your Title Here'}
          </h3>
          <p 
            className="mb-6 opacity-90 leading-relaxed whitespace-pre-line"
            style={{ 
              color: styles.textColor,
              fontSize: `${styles.bodyFontSize}px`
            }}
          >
            {content.body || 'This is the body text of your in-app message.'}
          </p>
          
          {content.showPrimaryButton && (
            <button
              className="py-3 px-6 font-semibold w-full active:scale-95 transition-transform shadow-sm"
              style={{ 
                backgroundColor: styles.buttonColor, 
                color: styles.buttonTextColor,
                borderRadius: 8
              }}
            >
              {content.buttonText || 'Action'}
            </button>
          )}

          {showSecondary && (
             <button 
                className="mt-3 py-3 px-6 text-sm font-medium w-full hover:opacity-80 active:scale-95 transition-all" 
                style={{ 
                    backgroundColor: styles.secondaryButtonColor,
                    color: styles.secondaryButtonTextColor,
                    borderRadius: 8
                }}
             >
               {content.secondaryButtonText || 'Close'}
             </button>
          )}
        </div>
      </div>
    );
  };

  const renderWrapper = () => {
    switch (type) {
      case TemplateType.TOP_BANNER:
        const hasImage = content.showImage && content.imageUrl;
        return (
          <div className="absolute top-0 left-0 right-0 p-4 pt-12 z-20">
             <div 
              className="shadow-xl flex flex-row items-center p-4 gap-4 cursor-pointer active:opacity-90 transition-opacity"
              style={{ 
                backgroundColor: styles.backgroundColor, 
                borderRadius: styles.borderRadius 
              }}
              onClick={() => {
                // Handler for click - will be handled by primary action in real app
              }}
            >
               {hasImage && (
                <img 
                  src={content.imageUrl} 
                  className="object-cover flex-shrink-0" 
                  style={{
                    width: `${styles.imageSize || 48}px`,
                    height: `${styles.imageSize || 48}px`,
                    borderRadius: `${styles.imageBorderRadius || 24}px`
                  }}
                />
               )}
               <div className={hasImage ? "flex-1 min-w-0" : "flex-1 min-w-0 text-center"}>
                 <h4 className={`font-bold ${hasImage ? 'truncate' : ''}`} style={{ color: styles.textColor, fontSize: `${Math.max(14, styles.titleFontSize * 0.7)}px` }}>{content.title}</h4>
                 <p className={`mt-1 ${hasImage ? 'line-clamp-2' : ''}`} style={{ color: styles.textColor, opacity: 0.8, fontSize: `${Math.max(12, styles.bodyFontSize * 0.75)}px` }}>{content.body}</p>
               </div>
            </div>
          </div>
        );

      case TemplateType.CENTER_MODAL:
        return (
          <div 
            className="absolute inset-0 flex items-center justify-center z-20 p-8"
            style={{ backgroundColor: styles.overlayColor }}
          >
            <div 
              className="w-full max-w-[320px] shadow-2xl relative"
              style={{ 
                backgroundColor: styles.backgroundColor,
                borderRadius: styles.borderRadius,
              }}
            >
              {renderContent()}
            </div>
          </div>
        );

      case TemplateType.FULL_SCREEN:
        return (
          <div 
            className="absolute inset-0 z-20 pt-10"
            style={{ backgroundColor: styles.backgroundColor }}
          >
            {renderContent()}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="transform scale-[0.8] sm:scale-90 md:scale-100 origin-top md:origin-center h-full flex justify-center">
      <div className="relative w-[360px] h-[740px] bg-slate-900 rounded-[50px] shadow-2xl border-[8px] border-black overflow-hidden ring-1 ring-black/50 flex-shrink-0">
        
        <div className="absolute top-0 inset-x-0 h-7 bg-transparent z-50 flex justify-between items-center px-6">
           <span className="text-[10px] font-medium text-white ml-2">9:41</span>
           <div className="w-20 h-6 bg-black rounded-b-2xl absolute left-1/2 -translate-x-1/2 top-0"></div>
           <div className="flex gap-1.5 text-white mr-1">
              <Signal size={12} />
              <Wifi size={12} />
              <Battery size={12} />
           </div>
        </div>

        <div className="w-full h-full relative overflow-hidden font-sans" style={{ backgroundColor: colors.background }}>
          
          <div className="absolute inset-0 z-0 flex flex-col pointer-events-none" style={{ backgroundColor: colors.backgroundSecondary }}>
             <div className="h-16 border-b flex items-end pb-3 px-4 shadow-sm" style={{ backgroundColor: colors.background, borderColor: colors.border }}>
                <div className="w-6 h-6 rounded-full" style={{ backgroundColor: colors.backgroundTertiary }}></div>
                <div className="flex-1"></div>
                <div className="w-6 h-6 rounded-full" style={{ backgroundColor: colors.backgroundTertiary }}></div>
             </div>
             <div className="p-4 space-y-4 overflow-hidden opacity-30">
                <div className="h-40 rounded-xl w-full" style={{ backgroundColor: colors.backgroundTertiary }}></div>
                <div className="space-y-2">
                   <div className="h-4 rounded w-3/4" style={{ backgroundColor: colors.backgroundTertiary }}></div>
                   <div className="h-4 rounded w-1/2" style={{ backgroundColor: colors.backgroundTertiary }}></div>
                   <div className="h-4 rounded w-2/3" style={{ backgroundColor: colors.backgroundTertiary }}></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-32 rounded-xl" style={{ backgroundColor: colors.backgroundTertiary }}></div>
                  <div className="h-32 rounded-xl" style={{ backgroundColor: colors.backgroundTertiary }}></div>
                </div>
             </div>
          </div>

          {renderWrapper()}

        </div>
        
        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-32 h-1 bg-white/20 rounded-full z-50"></div>
      </div>
    </div>
  );
};

